import { useState, useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import Lenis from 'lenis'
import './App.css'
import Navigation from './components/Navigation'
import Hero from './components/Hero'
import About from './components/About'
import CareerTimeline from './components/CareerTimeline'
import CaseStudies from './components/CaseStudies'
import Services from './components/Services'
import Testimonials from './components/Testimonials'
import Skills from './components/Skills'
import Contact from './components/Contact'
import CommandPalette from './components/CommandPalette'
import Footer from './components/Footer'
import AskAkshay from './components/AskAkshay'
import ServicesDrawer from './components/ServicesDrawer'
import PaymentSuccess from './components/PaymentSuccess'
import PaymentCancel from './components/PaymentCancel'
import Blog from './components/Blog'
import BlogPost from './components/BlogPost'
import Resume from './components/Resume'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

function Portfolio() {
  const [commandOpen, setCommandOpen] = useState(false)
  const [selectedService, setSelectedService] = useState(null)
  const lenisRef = useRef(null)

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true })
    lenisRef.current = lenis
    window.__lenis = lenis
    let rafId
    function raf(time) { lenis.raf(time); rafId = requestAnimationFrame(raf) }
    rafId = requestAnimationFrame(raf)
    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCommandOpen(true) }
      if (e.key === 'Escape') setCommandOpen(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => { lenis.destroy(); cancelAnimationFrame(rafId); window.removeEventListener('keydown', handleKey) }
  }, [])

  const scrollToSection = (id) => {
    const el = document.getElementById(id)
    if (!el) return
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 })
    else el.scrollIntoView({ behavior: 'smooth' })
    setCommandOpen(false)
  }

  const handleSelectService = (serviceName) => {
    setSelectedService(serviceName)
    scrollToSection('contact')
  }

  const handleBookConsultation = async () => {
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      })
      if (!res.ok) throw new Error('Checkout failed')
      const { checkout_url } = await res.json()
      window.location.href = checkout_url
    } catch (e) {
      // Fallback to contact form on error
      scrollToSection('contact')
    }
  }

  return (
    <div className="App min-h-screen bg-[#FAFAFA] text-[#111111] overflow-x-hidden">
      <Navigation onCommandOpen={() => setCommandOpen(true)} onNavigate={scrollToSection} />
      <main className="pb-20 lg:pb-0">
        <Hero onNavigate={scrollToSection} />
        <About />
        <CareerTimeline />
        <CaseStudies />
        <Services onSelectService={handleSelectService} onBookConsultation={handleBookConsultation} />
        <Testimonials />
        <Skills />
        <Contact selectedService={selectedService} onClearService={() => setSelectedService(null)} />
      </main>
      <Footer onNavigate={scrollToSection} />
      <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} onNavigate={scrollToSection} />
      <AskAkshay />
      <ServicesDrawer
        onNavigate={scrollToSection}
        onSelectService={handleSelectService}
        onBookConsultation={handleBookConsultation}
      />
    </div>
  )
}

function App() {
  const scrollToSection = (id) => {
    window.location.href = `/#${id}`
  }
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Portfolio />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/resume" element={<Resume />} />
        <Route path="/payment/success" element={<PaymentSuccess onNavigate={scrollToSection} />} />
        <Route path="/payment/cancel" element={<PaymentCancel onNavigate={scrollToSection} />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
